/**
 * In-memory Audit Hub mock matching the Audit Hub OpenAPI request/response shapes.
 * Used when REACT_APP_AUDIT_HUB_MOCK is not "false".
 */

import { AUDIT_STATUSES } from "../constants";

const STORAGE_KEY = "cloudcatcher:audit-hub-openapi-mock";

const okGet = (body) => body;
const okPost = (body, status = 200) => ({ data: body, status });
const fail = (status, code, message, field = null) => ({
  status,
  data: { error: { code, message, field } },
});

const load = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { audits: [], jobs: {}, pollCount: {} };
    const parsed = JSON.parse(raw);
    return {
      audits: Array.isArray(parsed.audits) ? parsed.audits : [],
      jobs: parsed.jobs && typeof parsed.jobs === "object" ? parsed.jobs : {},
      pollCount: parsed.pollCount && typeof parsed.pollCount === "object" ? parsed.pollCount : {},
    };
  } catch (_e) {
    return { audits: [], jobs: {}, pollCount: {} };
  }
};

const save = (state) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (_e) {
    /* ignore */
  }
};

const emptyProgress = () => ({ total: 0, decided: 0, inReview: 0, outstanding: 0 });
const emptyComponents = () => ({
  passedControls: 0,
  failedControls: 0,
  manualPassed: 0,
  manualFailed: 0,
  manualNotApplicable: 0,
  manualUndecided: 0,
});

const CATALOG = [
  { complianceAddonId: 1, complianceKey: "cis_5.0_aws", name: "CIS", isScanEnabled: true, manualControlCount: 12 },
  { complianceAddonId: 2, complianceKey: "nist_csf_2.0_aws", name: "NIST CSF", isScanEnabled: true, manualControlCount: 8 },
  { complianceAddonId: 3, complianceKey: "soc2_aws", name: "SOC 2", isScanEnabled: true, manualControlCount: 0 },
];

const toSummary = (detail) => ({
  auditId: detail.auditId,
  auditCode: detail.auditCode,
  name: detail.name,
  status: detail.status,
  accountCount: detail.accountCount ?? (detail.accounts || []).length,
  scopeCount:
    detail.scopeCount ??
    (detail.accounts || []).reduce((sum, account) => sum + (account.scopes || []).length, 0),
  ownerEmail: detail.ownerEmail,
  dueDate: detail.dueDate ?? null,
  progress: detail.progress || emptyProgress(),
  baseScore: detail.baseScore ?? null,
  auditScore: detail.auditScore ?? null,
  lastActivityAt: detail.lastActivityAt ?? null,
  isStale: Boolean(detail.isStale),
});

const nextCode = (audits) => {
  const max = audits.reduce((acc, item) => {
    const match = String(item.auditCode || "").match(/^AUD-(\d+)$/i);
    return match ? Math.max(acc, Number(match[1])) : acc;
  }, 100);
  return `AUD-${String(max + 1).padStart(3, "0")}`;
};

const findAudit = (state, auditId) => state.audits.find((a) => a.auditId === auditId);

const containsAccount = (detail, accountNumber) =>
  (detail.accounts || []).some((account) => account.accountNumber === accountNumber) ||
  detail.accountNumber === accountNumber;

const containsFramework = (detail, complianceKey) =>
  (detail.accounts || []).some((account) =>
    (account.scopes || []).some((scope) => scope.framework?.complianceKey === complianceKey)
  ) || detail.framework?.complianceKey === complianceKey;

export const mockListFrameworks = async (params = {}) => {
  const page = Number(params.page) || 1;
  const size = Number(params.size) || 100;
  const start = (page - 1) * size;
  return okGet({
    page,
    size,
    total: CATALOG.length,
    items: CATALOG.slice(start, start + size),
  });
};

const utcDayKey = (date) => date.toISOString().slice(0, 10);

const sortMissingLast = (left, right, direction) => {
  const leftMissing = left == null || left === "";
  const rightMissing = right == null || right === "";
  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;
  if (typeof left === "number" && typeof right === "number") return (left - right) * direction;
  return String(left).localeCompare(String(right), undefined, { sensitivity: "base", numeric: true }) * direction;
};

export const mockListAudits = async (params = {}) => {
  const state = load();
  const details = state.audits;
  const byId = new Map(details.map((detail) => [detail.auditId, detail]));
  let items = details.map(toSummary);
  if (params.status) {
    items = items.filter((a) => a.status === params.status);
  }
  if (String(params.excludeDrafts) === "true") {
    items = items.filter((a) => a.status !== AUDIT_STATUSES.DRAFT);
  }
  if (params.accountNumber) {
    const ids = new Set(
      details.filter((detail) => containsAccount(detail, params.accountNumber)).map((d) => d.auditId)
    );
    items = items.filter((a) => ids.has(a.auditId));
  }
  if (params.complianceKey) {
    const ids = new Set(
      details.filter((detail) => containsFramework(detail, params.complianceKey)).map((d) => d.auditId)
    );
    items = items.filter((a) => ids.has(a.auditId));
  }
  if (params.q) {
    const needle = String(params.q).trim().toLowerCase();
    if (needle) {
      items = items.filter((item) =>
        `${item.auditCode || ""} ${item.name || ""} ${item.ownerEmail || ""}`.toLowerCase().includes(needle)
      );
    }
  }
  if (params.dueWithinDays != null && params.dueWithinDays !== "") {
    const days = Number(params.dueWithinDays);
    const limit = new Date();
    limit.setUTCDate(limit.getUTCDate() + days);
    const limitKey = utcDayKey(limit);
    items = items.filter((item) => item.dueDate && String(item.dueDate).slice(0, 10) <= limitKey);
  }
  const sort = params.sort || "createdAt";
  const direction = params.order === "asc" ? 1 : -1;
  const sortValue = (item) => {
    const detail = byId.get(item.auditId);
    if (sort === "dueDate") return item.dueDate || null;
    if (sort === "name") return item.name || null;
    if (sort === "auditCode") return item.auditCode || null;
    if (sort === "status") return item.status || null;
    if (sort === "lastActivityAt") return item.lastActivityAt || null;
    if (sort === "ownerEmail") return item.ownerEmail || null;
    if (sort === "defaultAssigneeEmail") return detail?.defaultAssigneeEmail || null;
    if (sort === "accountCount") return item.accountCount ?? null;
    if (sort === "scopeCount") return item.scopeCount ?? null;
    if (sort === "manualTotal") return item.progress?.total ?? null;
    if (sort === "manualDecided") return item.progress?.decided ?? null;
    if (sort === "manualUndecided") return item.progress?.outstanding ?? null;
    return detail?.createdAt || null;
  };
  items.sort((left, right) => sortMissingLast(sortValue(left), sortValue(right), direction));
  const page = Number(params.page) || 1;
  const size = Number(params.size) || 25;
  const start = (page - 1) * size;
  return okGet({
    page,
    size,
    total: items.length,
    items: items.slice(start, start + size),
  });
};

const buildAccount = (detail, entry) => {
  if (!/^\d{12}$/.test(String(entry.accountNumber || ""))) {
    return fail(400, "VALIDATION_ERROR", "accountNumber must be 12 digits.", "accountNumber");
  }
  if ((detail.accounts || []).some((account) => account.accountNumber === entry.accountNumber)) {
    return fail(409, "ACCOUNT_ALREADY_IN_AUDIT", "That account is already in this assessment.");
  }
  const ids = entry.complianceAddonIds;
  if (!Array.isArray(ids) || ids.length < 1) {
    return fail(400, "NO_SCOPES", "Each account needs at least one framework.", "complianceAddonIds");
  }
  if (ids.length > 1) {
    return fail(400, "ONE_FRAMEWORK_PER_AUDIT", "An assessment covers one framework.", "complianceAddonIds");
  }
  const requestedId = Number(ids[0]);
  const existingId = (detail.accounts || [])
    .flatMap((account) => account.scopes || [])
    .map((scope) => Number(scope.framework?.complianceAddonId))
    .find((id) => Number.isInteger(id));
  if (Number.isInteger(existingId) && existingId !== requestedId) {
    const existing = CATALOG.find((item) => item.complianceAddonId === existingId);
    const name = existing?.name || `framework ${existingId}`;
    return fail(409, "ONE_FRAMEWORK_PER_AUDIT", `This assessment is already on ${name}.`);
  }
  const seenIds = new Set();
  const scopes = [];
  const auditAccountId = `aac-${Date.now().toString(36)}-${(detail.accounts || []).length}`;
  for (const rawId of ids) {
    const complianceAddonId = Number(rawId);
    if (!Number.isInteger(complianceAddonId)) {
      return fail(400, "VALIDATION_ERROR", "complianceAddonIds must be integers.", "complianceAddonIds");
    }
    if (seenIds.has(complianceAddonId)) {
      return fail(409, "FRAMEWORK_ALREADY_IN_SCOPE", "The same framework is listed twice on one account.");
    }
    seenIds.add(complianceAddonId);
    const catalog = CATALOG.find((item) => item.complianceAddonId === complianceAddonId);
    scopes.push({
      auditScopeId: `scp-${complianceAddonId}-${scopes.length}`,
      auditAccountId,
      framework: {
        complianceAddonId,
        complianceKey: catalog?.complianceKey || `addon-${complianceAddonId}`,
        name: catalog?.name || null,
        version: null,
      },
      hasScanResult: false,
      latestRunId: null,
      latestRunEndedAt: null,
      progress: emptyProgress(),
      baseScore: null,
      auditScore: null,
      components: emptyComponents(),
    });
  }
  return {
    auditAccountId,
    accountNumber: entry.accountNumber,
    accountName: null,
    accountCode: null,
    regions: Array.isArray(entry.regions) ? entry.regions : [],
    progress: emptyProgress(),
    scheduleId: null,
    scopes,
  };
};

const refreshCounts = (detail) => {
  const accounts = detail.accounts || [];
  detail.accountCount = accounts.length;
  detail.scopeCount = accounts.reduce((sum, account) => sum + (account.scopes || []).length, 0);
};

export const mockCreateAudit = async (body = {}) => {
  if (!body.name || !body.ownerEmail) {
    return fail(400, "VALIDATION_ERROR", "name and ownerEmail are required.");
  }
  const state = load();
  const now = new Date().toISOString();
  const detail = {
    auditId: `aud-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    auditCode: nextCode(state.audits),
    name: body.name,
    status: AUDIT_STATUSES.DRAFT,
    accountCount: 0,
    scopeCount: 0,
    ownerEmail: body.ownerEmail,
    dueDate: body.dueDate || null,
    progress: emptyProgress(),
    baseScore: null,
    auditScore: null,
    lastActivityAt: now,
    createdAt: now,
    isStale: false,
    defaultAssigneeEmail: body.defaultAssigneeEmail || null,
    auditorEmails: Array.isArray(body.auditorEmails) && body.auditorEmails.length ? body.auditorEmails : [body.ownerEmail],
    startedAt: null,
    completedAt: null,
    scoredScopeCount: 0,
    components: emptyComponents(),
    statusCounts: {},
    accounts: [],
    scanDispatchFailures: [],
  };
  state.audits.unshift(detail);
  save(state);
  return okPost(detail, 201);
};

export const mockAddAccount = async (auditId, body = {}) => {
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Assessment not found.");
  if (detail.status !== AUDIT_STATUSES.DRAFT) {
    return fail(409, "AUDIT_NOT_DRAFT", "Accounts can be added only while the assessment is a draft.");
  }
  const account = buildAccount(detail, body);
  if (account.status) return account;
  detail.accounts.push(account);
  refreshCounts(detail);
  detail.lastActivityAt = new Date().toISOString();
  save(state);
  return okPost(
    {
      auditAccountId: account.auditAccountId,
      accountNumber: account.accountNumber,
      scopeCount: account.scopes.length,
      controlCount: 0,
      scan: null,
      scanDispatchFailed: false,
    },
    201
  );
};

export const mockAddFrameworks = async (auditId, auditAccountId, complianceAddonIds) => {
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Assessment not found.");
  if (detail.status !== AUDIT_STATUSES.DRAFT) {
    return fail(409, "AUDIT_NOT_DRAFT", "Frameworks can be added only while the assessment is a draft.");
  }
  const account = (detail.accounts || []).find((item) => item.auditAccountId === auditAccountId);
  if (!account) return fail(404, "AUDIT_ACCOUNT_NOT_FOUND", "That account is not in this assessment.");
  const ids = complianceAddonIds;
  if (!Array.isArray(ids) || ids.length < 1) {
    return fail(400, "VALIDATION_ERROR", "Choose a framework.", "complianceAddonIds");
  }
  if (ids.length > 1) {
    return fail(400, "ONE_FRAMEWORK_PER_AUDIT", "An assessment covers one framework.", "complianceAddonIds");
  }
  const requestedId = Number(ids[0]);
  const existingId = (detail.accounts || [])
    .flatMap((item) => item.scopes || [])
    .map((scope) => Number(scope.framework?.complianceAddonId))
    .find((id) => Number.isInteger(id));
  if (Number.isInteger(existingId) && existingId !== requestedId) {
    const existing = CATALOG.find((item) => item.complianceAddonId === existingId);
    const name = existing?.name || `framework ${existingId}`;
    return fail(409, "ONE_FRAMEWORK_PER_AUDIT", `This assessment is already on ${name}.`);
  }
  if ((account.scopes || []).some((scope) => Number(scope.framework?.complianceAddonId) === requestedId)) {
    return fail(409, "FRAMEWORK_ALREADY_IN_SCOPE", "That framework is already on this account.");
  }
  const catalog = CATALOG.find((item) => item.complianceAddonId === requestedId);
  account.scopes = [
    ...(account.scopes || []),
    {
      auditScopeId: `scp-${requestedId}-${Date.now().toString(36)}`,
      auditAccountId,
      framework: {
        complianceAddonId: requestedId,
        complianceKey: catalog?.complianceKey || `addon-${requestedId}`,
        name: catalog?.name || null,
        version: null,
      },
      hasScanResult: false,
      latestRunId: null,
      latestRunEndedAt: null,
      progress: emptyProgress(),
      baseScore: null,
      auditScore: null,
      components: emptyComponents(),
    },
  ];
  refreshCounts(detail);
  save(state);
  return okPost({ auditAccountId, scopeCount: account.scopes.length }, 201);
};

export const mockLaunchAudit = async (auditId) => {
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Assessment not found.");
  if (detail.status !== AUDIT_STATUSES.DRAFT) {
    return fail(409, "AUDIT_NOT_DRAFT", "This assessment has already been launched.");
  }
  if (!detail.accounts.length) {
    return fail(400, "NO_ACCOUNTS", "Add an account before launching this assessment.");
  }
  detail.status = AUDIT_STATUSES.ACTIVE;
  detail.startedAt = new Date().toISOString();
  detail.lastActivityAt = detail.startedAt;
  detail.scanDispatchFailures = [];
  save(state);
  return okPost(detail, 200);
};

export const mockDeleteDraft = async (auditId) => {
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Assessment not found.");
  if (detail.status !== AUDIT_STATUSES.DRAFT) {
    return fail(409, "AUDIT_NOT_DRAFT", "Only a draft can be deleted.");
  }
  state.audits = state.audits.filter((audit) => audit.auditId !== auditId);
  save(state);
  return { data: null, status: 204 };
};

export const mockGetAudit = async (auditId) => {
  const detail = findAudit(load(), auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Assessment not found.");
  return okGet(detail);
};

export const mockUpdateAudit = async (auditId, body = {}) => {
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Assessment not found.");
  if (detail.status === AUDIT_STATUSES.COMPLETED || detail.status === AUDIT_STATUSES.CANCELLED) {
    return fail(409, "AUDIT_NOT_ACTIVE", "The assessment is completed or cancelled.");
  }
  const touched =
    body.name != null ||
    body.ownerEmail != null ||
    body.defaultAssigneeEmail !== undefined ||
    body.dueDate !== undefined;
  if (!touched) return okGet(detail);
  if (body.name != null) detail.name = body.name;
  if (body.ownerEmail != null) detail.ownerEmail = body.ownerEmail;
  if (body.defaultAssigneeEmail !== undefined) detail.defaultAssigneeEmail = body.defaultAssigneeEmail;
  if (body.dueDate !== undefined) detail.dueDate = body.dueDate;
  detail.lastActivityAt = new Date().toISOString();
  save(state);
  return okGet(detail);
};

export const mockUpdateAccount = async (auditId, auditAccountId, body = {}) => {
  if (!Array.isArray(body?.regions)) {
    return fail(400, "VALIDATION_ERROR", "regions is required.", "regions");
  }
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Assessment not found.");
  if (detail.status !== AUDIT_STATUSES.DRAFT) {
    return fail(409, "AUDIT_NOT_DRAFT", "Regions can be changed only while the assessment is a draft.");
  }
  const account = (detail.accounts || []).find((item) => item.auditAccountId === auditAccountId);
  if (!account) return fail(404, "AUDIT_ACCOUNT_NOT_FOUND", "That account is not in this assessment.");
  const next = body.regions.filter((region) => typeof region === "string");
  const current = Array.isArray(account.regions) ? account.regions : [];
  const unchanged = next.length === current.length && next.every((region, index) => region === current[index]);
  if (unchanged) return okGet(detail);
  account.regions = next;
  save(state);
  return okGet(detail);
};

export const mockSetAuditors = async (auditId, body = {}) => {
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Assessment not found.");
  if (detail.status !== AUDIT_STATUSES.DRAFT) {
    return fail(409, "AUDITORS_FIXED", "Auditors can be changed only while the assessment is a draft.");
  }
  const emails = Array.isArray(body.auditorEmails) ? body.auditorEmails.filter(Boolean) : [];
  if (!emails.length) return fail(400, "NO_AUDITORS", "Choose at least one auditor.", "auditorEmails");
  detail.auditorEmails = emails;
  detail.lastActivityAt = new Date().toISOString();
  save(state);
  return okGet(detail);
};

export const mockCompleteAudit = async (auditId) => {
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Assessment not found.");
  detail.status = AUDIT_STATUSES.COMPLETED;
  detail.completedAt = new Date().toISOString();
  detail.lastActivityAt = detail.completedAt;
  save(state);
  return okPost(detail);
};

export const mockCancelAudit = async (auditId, body = {}) => {
  if (!body.reason) return fail(400, "VALIDATION_ERROR", "reason is required.", "reason");
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Assessment not found.");
  detail.status = AUDIT_STATUSES.CANCELLED;
  detail.lastActivityAt = new Date().toISOString();
  save(state);
  return okPost(detail);
};

export const mockRequestReport = async (auditId) => {
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Assessment not found.");
  if (detail.status !== AUDIT_STATUSES.COMPLETED) {
    return fail(409, "AUDIT_NOT_COMPLETED", "A report is available after the assessment is completed.");
  }
  const job = {
    jobId: `job-${Date.now().toString(36)}`,
    kind: "GENERATE_REPORT",
    status: "QUEUED",
    createdAt: new Date().toISOString(),
    completedAt: null,
    auditId,
    downloadUrl: null,
    requestedBy: "mock@local",
    error: null,
  };
  state.jobs[job.jobId] = job;
  state.pollCount[job.jobId] = 0;
  save(state);
  return okPost(job, 202);
};

export const mockGetJob = async (auditId, jobId) => {
  const state = load();
  const job = state.jobs[jobId];
  if (!job || job.auditId !== auditId) return fail(404, "JOB_NOT_FOUND", "Job not found.");
  state.pollCount[jobId] = (state.pollCount[jobId] || 0) + 1;
  if (state.pollCount[jobId] >= 2 && job.status !== "READY" && job.status !== "FAILED") {
    job.status = "READY";
    job.completedAt = new Date().toISOString();
    job.downloadUrl = URL.createObjectURL(
      new Blob([`Audit report placeholder for ${auditId}`], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      })
    );
  }
  save(state);
  return okGet(job);
};
